import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { isSameDay } from 'date-fns';
import { LucidePlus, LucideClock, LucidePlusCircle } from 'lucide-react';
import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker } from 'src/components/DatePicker';
import { trpc, Container, Truck, Driver, ContainerInput, Order } from 'src/trpc';
import { LuTriangleAlert, LuX } from 'react-icons/lu';
import { useToast } from 'components/useToast';
import { StatusDisplay } from 'src/components/StatusDisplay';
import { OrderAction } from '@libs/schemas/src/order';
import { timeRanges } from 'src/utils/time-ranges';
import { Loader } from 'src/components/Loader';
import { toNoonUTC, toValidDate } from '@libs/frontend-services';

const earliestDropOffDate = new Date();

interface ContainerFormProps {
  setContainerInput: (container: Partial<ContainerInput>) => void;
  selectedContainerIds: string[];
  initialContainerInput?: Partial<ContainerInput>;
  containers: Container[];
  drivers: Driver[];
  trucks: Truck[];
  action: OrderAction;
  /** Assignment editing is unavailable while the order is being written, so the add/remove
   *  controls stay disabled rather than queuing edits against a row that is mid-save. */
  isSubmitting?: boolean;
}

export const ContainerForm: FC<ContainerFormProps> = ({
  setContainerInput,
  initialContainerInput,
  containers,
  selectedContainerIds,
  trucks,
  drivers,
  action,
  isSubmitting = false,
}) => {
  const navigate = useNavigate();
  const [numOfContainers, setNumOfContainers] = useState(
    initialContainerInput?.containerIds?.length || 1,
  );
  const [containerIds, setContainerIds] = useState<string[]>(
    initialContainerInput?.containerIds || [],
  );
  // Default to the first available truck/driver so admins don't have to pick one
  // on every assignment. The available lists can include unavailable resources
  // (e.g. an existing assignment kept visible via includeIds), so only default to
  // a resource whose status is available. An explicit initial assignment is always
  // preserved regardless of its status. Both stay editable via the selects below.
  const [truckId, setTruckId] = useState<string>(
    initialContainerInput?.truckId || trucks.find((t) => t.status === 'available')?._id || '',
  );
  const [driverId, setDriverId] = useState<string>(
    initialContainerInput?.driverId || drivers.find((d) => d.status === 'available')?._id || '',
  );
  const [dropOffDate, setDropOffDate] = useState<Date | undefined>(
    // A present window does not guarantee a day: Invalid Date makes the picker throw.
    toValidDate(initialContainerInput?.window?.day) ?? undefined,
  );
  // Always use 6:00 AM - 6:00 PM time window
  const dropOffTime = '6:00 AM - 6:00 PM';

  const canAddContainer = numOfContainers < containers.length;
  // Each assignment row's select trigger, so focus can follow a row that was just added and
  // does not get stranded on the add control when that control disappears at capacity.
  const selectTriggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Set to the row that should take focus on the next render, cleared once it has.
  const [rowToFocus, setRowToFocus] = useState<number | null>(null);
  // Announced politely rather than shown: the new row is already visible on screen.
  const [assignmentAnnouncement, setAssignmentAnnouncement] = useState('');
  // The parent supplies a fresh callback on every update. Track its latest value
  // without making that callback (or the unrelated selected IDs) re-trigger the
  // effect and continuously re-submit identical form state.
  const onInputChange = useRef(setContainerInput);
  useEffect(() => {
    onInputChange.current = setContainerInput;
  }, [setContainerInput]);

  useEffect(() => {
    if (rowToFocus === null) return;
    selectTriggerRefs.current[rowToFocus]?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  // Functional updates throughout: two activations landing in the same batch must add two rows,
  // not one, and a removal must not be computed from a count that a queued add already changed.
  const addAssignment = useCallback(() => {
    setNumOfContainers((count) => {
      const next = count + 1;
      setRowToFocus(next - 1);
      setAssignmentAnnouncement(`Dumpster assignment ${next} added`);
      return next;
    });
  }, []);

  const removeAssignment = useCallback((index: number) => {
    setNumOfContainers((count) => Math.max(1, count - 1));
    setContainerIds((ids) => ids.filter((_, idx) => idx !== index));
    // The button that was just activated is gone, so focus lands on the row above rather than
    // falling back to the document.
    setRowToFocus(Math.max(0, index - 1));
    setAssignmentAnnouncement(`Dumpster assignment ${index + 1} removed`);
  }, []);

  useEffect(() => {
    // Find the 6:00 AM - 6:00 PM time range
    const timeRange = timeRanges.find((t) => t.label === '6:00 AM - 6:00 PM')!;

    onInputChange.current({
      containerIds,
      truckId,
      driverId,
      ...(dropOffDate
        ? {
            window: {
              day: toNoonUTC(dropOffDate),
              start: timeRange.start,
              end: timeRange.end,
            },
          }
        : {}),
    });
  }, [containerIds, truckId, driverId, dropOffDate]);

  const handleAddResource = (resourceType: string) => {
    switch (resourceType) {
      case 'container':
        navigate('/containers');
        break;
      case 'truck':
        navigate('/trucks');
        break;
      case 'driver':
        navigate('/drivers');
        break;
      default:
        break;
    }
  };

  return (
    <div className={'w-full flex flex-col gap-4'}>
      {/* The new row is visible, so its arrival is announced rather than displayed. */}
      <div role="status" aria-live="polite" className="sr-only">
        {assignmentAnnouncement}
      </div>
      {canAddContainer && (
        // Absolute positioning only survives at `sm` and above. Below it the control takes its
        // own row in normal flow, so a label wrapped at the 1.5x mobile type scale grows the
        // button and pushes the first field down instead of landing on top of it.
        <Button
          type="button"
          variant={'ghost'}
          disabled={isSubmitting}
          // `h-auto` on top of the 44px floor: the button's height has to follow a label
          // wrapped at 1.5x rather than clip it inside the 40px default box.
          className="text-muted flex flex-row gap-1 items-center static w-full h-auto min-h-11 justify-center whitespace-normal sm:absolute sm:right-4 sm:top-2 sm:w-auto"
          onClick={addAssignment}
        >
          <LucidePlus className="w-5 h-5 text-muted shrink-0" aria-hidden="true" />
          <p>Assign another dumpster</p>
        </Button>
      )}
      {[...Array(numOfContainers).keys()].map((i) => (
        <div className="w-full" key={i}>
          <div className="flex flex-row gap-4 items-center">
            {/* The visible label stays "Assign Dumpster" on every row; the row number is
                appended for assistive tech so repeated selects are distinguishable. */}
            <label className="label" id={`assign-dumpster-label-${i}`}>
              Assign Dumpster<span className="sr-only"> {i + 1}</span>
            </label>
            {numOfContainers > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isSubmitting}
                aria-label={`Remove dumpster assignment ${i + 1}`}
                className="h-11 w-11 min-h-11 min-w-11 shrink-0 text-muted"
                onClick={() => removeAssignment(i)}
              >
                <LuX className="w-5 h-5" aria-hidden="true" />
              </Button>
            )}
          </div>
          <Select
            defaultValue={initialContainerInput?.containerIds?.[i] || ''}
            onValueChange={(value) => {
              if (value === 'add_container') {
                handleAddResource('container');
                return;
              }
              containerIds[i] = value;
              setContainerIds([...containerIds]);
            }}
          >
            <SelectTrigger
              className="text-text-light"
              aria-labelledby={`assign-dumpster-label-${i}`}
              ref={(node) => {
                selectTriggerRefs.current[i] = node;
              }}
            >
              <div className="flex flex-row gap-2">
                <SelectValue placeholder={'Select dumpster'} />
              </div>
            </SelectTrigger>
            <SelectContent>
              {containers.length === 0 ? (
                <SelectItem key="add_container" value="add_container" className="font-medium">
                  + Create new dumpster
                </SelectItem>
              ) : (
                <>
                  {containers
                    .filter(
                      (c) => containerIds[i] === c._id || !selectedContainerIds.includes(c._id),
                    )
                    .map((c) => (
                      <SelectItem key={c._id} value={c._id}>
                        <div className="flex flex-row gap-1">
                          <p>#{c.friendlyId} /</p>
                          <p>{c.containerTypeInfo.name} /</p>
                          <StatusDisplay status={c.status} />
                        </div>
                      </SelectItem>
                    ))}
                  <SelectItem
                    key="add_container"
                    value="add_container"
                    className="font-medium border-t mt-1 pt-1"
                  >
                    + Create new dumpster
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div className="w-full">
        <label className="label w-full">Assign Truck</label>
        <Select
          value={truckId}
          onValueChange={(value) => {
            if (value === 'add_truck') {
              handleAddResource('truck');
              return;
            }
            setTruckId(value);
          }}
        >
          <SelectTrigger className="text-text-light">
            <div className="flex flex-row gap-2">
              <SelectValue placeholder={'Select truck'} />
            </div>
          </SelectTrigger>
          <SelectContent>
            {trucks.length === 0 ? (
              <SelectItem key="add_truck" value="add_truck" className="font-medium">
                + Add Truck
              </SelectItem>
            ) : (
              <>
                {trucks.map((t) => (
                  <SelectItem key={t._id} value={t._id}>
                    <div className="flex flex-row gap-1">
                      <p>#{t.friendlyId} /</p>
                      <p>
                        {t.size
                          .split('-')
                          .join(' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase())}{' '}
                        /
                      </p>
                      <StatusDisplay status={t.status} />
                    </div>
                  </SelectItem>
                ))}
                <SelectItem
                  key="add_truck"
                  value="add_truck"
                  className="font-medium border-t mt-1 pt-1"
                >
                  + Add Truck
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full">
        <label className="label w-full">Assign Driver</label>
        <Select
          value={driverId}
          onValueChange={(value) => {
            if (value === 'add_driver') {
              handleAddResource('driver');
              return;
            }
            setDriverId(value);
          }}
        >
          <SelectTrigger className="text-text-light">
            <div className="flex flex-row gap-2">
              <SelectValue placeholder={'Select driver'} />
            </div>
          </SelectTrigger>
          <SelectContent>
            {drivers.length === 0 ? (
              <SelectItem key="add_driver" value="add_driver" className="font-medium">
                + Add Driver
              </SelectItem>
            ) : (
              <>
                {drivers.map((d) => (
                  <SelectItem key={d._id} value={d._id ?? ''}>
                    <p>
                      {d.name.first} {d.name.last}
                    </p>
                  </SelectItem>
                ))}
                <SelectItem
                  key="add_driver"
                  value="add_driver"
                  className="font-medium border-t mt-1 pt-1"
                >
                  + Add Driver
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full flex flex-col gap-2">
        <label className="label">{action === 'drop-off' ? 'Drop-off Date' : 'Pick-up Date'}</label>
        <DatePicker date={dropOffDate} setDate={setDropOffDate} fromDate={earliestDropOffDate} />
      </div>
      <div className="flex flex-col gap-2">
        <label className="label">{action === 'drop-off' ? 'Drop-off Time' : 'Pick-up Time'}</label>
        <div
          data-testid="time-display"
          className="flex flex-row gap-2 items-center p-3 border rounded-md bg-muted/10"
        >
          <LucideClock className="w-5 h-5 text-muted" />
          <span className="text-sm text-text-light">{dropOffTime}</span>
        </div>
      </div>
    </div>
  );
};

interface Props {
  order: Order;
  action: OrderAction;
}

export const AssignContainerForm: FC<Props> = ({ order, action }) => {
  const { errorToast } = useToast();

  // Get already-assigned IDs from order to preserve them in queries
  const existingContainerIds =
    Array.from(new Set(order?.containers?.flatMap((c) => c.containerIds).filter(Boolean))) || [];
  const existingTruckIds =
    Array.from(
      new Set(order?.containers?.map((c) => c.truckId).filter((id): id is string => !!id)),
    ) || [];
  const existingDriverIds =
    Array.from(
      new Set(order?.containers?.map((c) => c.driverId).filter((id): id is string => !!id)),
    ) || [];

  const { data: allContainers, isFetched: containersFetched } =
    trpc.admin.container.getAvailableContainers.useQuery({
      limit: 100,
      includeIds: existingContainerIds,
    });

  const { data: drivers, isFetched: driversFetched } =
    trpc.admin.driver.getAvailableDrivers.useQuery({
      limit: 100,
      includeIds: existingDriverIds,
    });
  const { data: truckResponse, isFetched: trucksFetched } =
    trpc.admin.truck.getAvailableTrucks.useQuery({
      limit: 100,
      includeIds: existingTruckIds,
    });
  const filteredOrderContainers = order?.containers?.filter((c) => c.action === action);
  const containers =
    (action === 'drop-off'
      ? allContainers?.containers
      : allContainers?.containers.filter((c) =>
          order.containers?.some((co) => co.containerIds.includes(c._id)),
        )) || [];
  const [containerInputs, setContainerInputs] = useState<Partial<ContainerInput>[]>(
    filteredOrderContainers?.length
      ? filteredOrderContainers
          .filter((c) => c.action === action)
          .map((c) => ({
            containerIds: c.containerIds,
            truckId: c.truckId,
            window:
              c.window && toValidDate(c.window.day)
                ? { ...c.window, day: toValidDate(c.window.day)!.getTime() }
                : undefined,
            driverId: c.driverId,
          }))
      : [
          {
            containerIds: [],
            truckId: '',
            window: undefined,
            driverId: '',
          },
        ],
  );
  const hasDifferentTimes =
    action === 'drop-off' &&
    containerInputs.some(
      (c) =>
        c.window?.day &&
        !(
          c.window?.start === order.dropOffWindow?.start &&
          c.window?.end === order.dropOffWindow?.end &&
          isSameDay(new Date(c.window?.day!), new Date(order.dropOffWindow?.day!))
        ),
    );
  const params = useParams();
  const utils = trpc.useUtils();
  const { mutate, isPending } = trpc.admin.order.adminUpsertOrderContainers.useMutation({
    onSuccess: () => {
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(order._id);
    },
  });

  const isValid = containerInputs.every(
    (c) =>
      (c.containerIds?.length || 0) > 0 &&
      !!c.truckId &&
      !!c.driverId &&
      c.window?.day &&
      c.window?.start &&
      c.window?.end,
  );

  if (!containersFetched || !driversFetched || !trucksFetched) {
    return <div>Loading...</div>;
  }

  return (
    <div className="card w-full flex flex-col gap-6">
      {containerInputs.map((containerInput, i) => (
        <div className="card flex flex-col gap-2 bg-slate-100 w-full py-8 relative" key={i}>
          <ContainerForm
            containers={containers || []}
            drivers={drivers?.drivers || []}
            trucks={truckResponse?.trucks || []}
            initialContainerInput={containerInput}
            selectedContainerIds={containerInputs.map((c) => c.containerIds || []).flat()}
            setContainerInput={(c) => {
              containerInputs[i] = c;
              setContainerInputs([...containerInputs]);
            }}
            action={action}
            isSubmitting={isPending}
          />
        </div>
      ))}
      <div className="flex flex-col gap-6 w-full">
        {(action === 'drop-off' || containers.length > 1) && (
          <Button
            variant="outline"
            className="flex flex-row gap-2 text-text-light w-full"
            onClick={() =>
              setContainerInputs([
                ...containerInputs,
                {
                  containerIds: [],
                  truckId: '',
                  window: undefined,
                  driverId: '',
                },
              ])
            }
          >
            <LucidePlusCircle className="w-4 h-4" /> Add Truck & Driver
          </Button>
        )}
        <div className="h-[1px] bg-slate-200 w-full"></div>
        {hasDifferentTimes && (
          <div className="border-destructive text-destructive rounded-lg border bg-light-destructive px-4 py-3 flex flex-row justify-start gap-4 text-sm">
            <LuTriangleAlert className="w-8 h-8 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold">Heads up!</p>
              <p>
                Drop-off Date/Time is different than what the customer requested. Be sure to confirm
                with customer before updating.
              </p>
            </div>
          </div>
        )}
        <Button
          className="w-full"
          disabled={!isValid || isPending}
          onClick={() => {
            if (action === 'pickup') {
              const selectedContainers = containerInputs.map((c) => c.containerIds).flat();
              if (!containers.every((c) => selectedContainers.find((co) => co === c._id))) {
                errorToast({
                  title: 'All containers must be assigned to a truck and driver',
                  description: 'Please ensure every container has been assigned before proceeding.',
                });
                return;
              }
            }
            mutate({
              orderId: params.id!,
              containers: containerInputs as ContainerInput[],
              action: action as OrderAction,
            });
          }}
        >
          {isPending ? (
            <Loader h={4} w={4} color="text-white" align="horizontal" title="Updating Order..." />
          ) : (
            'Update Order'
          )}
        </Button>
      </div>
    </div>
  );
};
